import { now } from '@rematter/common/dateTime';
import { sumWeights } from '@rematter/common/sumWeights';
import {
  enumCurrencyCode,
  enumUnit,
  Money,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import {
  isDefined,
  isLocalId,
  localId,
  maybe,
  ensureValue,
} from '@rematter/ui-web';
import { getWeightValue } from '@rematter/ui-web/lib';
import Big from 'big.js';
import { atom } from 'jotai';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import unionBy from 'lodash/unionBy';
import { pipe } from 'pipe-ts';

import { loadReconciliationBatchUpdateAtom } from '../_graphql/mutationLoadReconciliationBatchUpdate';
import { deductionTypesQueryAtom } from '../_graphql/queryDeductionTypes';

import { $loadDetails, $loadId } from './loadDetails.state';

import { zeroMoney } from '~/modules/trade/utils/money';

// Convert Weight object to WeightInput
const convertWeightToWeightInput = (weight: Weight | null | undefined) => {
  if (!weight || weight.value === null || weight.value === undefined) {
    return { value: 0, unit: enumUnit.Pounds };
  }
  return {
    value: weight.value,
    unit: weight.unit,
  };
};

export type MoneyRecord = Omit<Money, '__typename'>;

export type LocalDeduction = {
  id: string;
  deductionTypeId: string;
  amount?: number;
  computedAmount?: number;
  weight?: Weight | null;
  receivedLineItemId?: string;
  deleted?: boolean;
  createdAt: string;
  synced?: boolean;
};

export type LocalReceivedItem = {
  id: string;
  lineItemId: string;
  materialId?: string;
  materialType?: string;
  grossWeight?: Weight | null;
  tareWeight?: Weight | null;
  netWeight?: Weight | null;
  deleted?: boolean;
  createdAt?: string;
  synced?: boolean;
  unitPrice?: MoneyRecord;
};

// 🔘🔘🔘 PLAIN ATOMS
const $localDeductions = atom<LocalDeduction[]>([]);
const $localReceivedItems = atom<LocalReceivedItem[]>([]);

// 🟢🟢🟢 REMOTE DATA -> deduction types
export const $deductionTypes = deductionTypesQueryAtom((get) => ({
  skip: !get($canEditReconciliation),
}));

export const $deductionTypesById = atom((get) =>
  keyBy(get($deductionTypes)?.data ?? [], (it) => it.id),
);

// 🟢🟢🟢 COMPUTED DATA -> received items by line item
export const $receivedLineItems = atom((get) => {
  const localReceivedItems = get($localReceivedItems);
  const remoteReceivedItems = get($loadDetails).data?.receivedLineItems?.map(
    (it): LocalReceivedItem => ({
      id: it.id,
      lineItemId: it.loadLineItemId,
      materialId: maybe(it.itemId),
      materialType: maybe(it.materialType),
      grossWeight: it.grossWeight
        ? { ...it.grossWeight, __typename: 'Weight' as const }
        : null,
      tareWeight: it.tareWeight
        ? { ...it.tareWeight, __typename: 'Weight' as const }
        : null,
      netWeight: it.netWeight
        ? { ...it.netWeight, __typename: 'Weight' as const }
        : null,
      createdAt: it.timestamps.createdAt,
      unitPrice: maybe(it.unitPrice),
    }),
  );

  const localReceivedItemsById = keyBy(localReceivedItems, (it) => it.id);
  const remoteReceivedItemsById = keyBy(remoteReceivedItems, (it) => it.id);

  return pipe(
    () => unionBy(localReceivedItems, remoteReceivedItems, (it) => it.id),
    (_) =>
      map(_, (item) => ({
        ...remoteReceivedItemsById[item.id],
        ...localReceivedItemsById[item.id],
      })),
    (_) => filter(_, (it) => !it.deleted),
    (_) => orderBy(_, (it) => it.createdAt),
  )();
});

// 🟢🟢🟢 COMPUTED DATA -> received items by line item
export const $receivedItemsByLineItem = atom((get) => {
  const receivedLineItems = get($receivedLineItems);
  return groupBy(receivedLineItems, (it) => it.lineItemId);
});

// 🟢🟢🟢 COMPUTED DATA -> deductions by line item
export const $currentDeductions = atom((get) => {
  const receivedLineItems = get($receivedLineItems);
  const localDeductions = get($localDeductions);
  const remoteDeductions = get($loadDetails).data?.deductions?.map(
    (it): LocalDeduction => ({
      id: it.id,
      deductionTypeId: it.deductionTypeId,
      amount: it.priceQuantity?.amount ?? 0,
      weight:
        typeof it.weightQuantity === 'object' && it.weightQuantity !== null
          ? { ...it.weightQuantity, __typename: 'Weight' as const }
          : it.weightQuantity
            ? {
                value: it.weightQuantity,
                unit: enumUnit.Pounds,
                __typename: 'Weight' as const,
              }
            : null,
      receivedLineItemId: maybe(it.receivedLineItemId),
      createdAt: it.timestamps?.createdAt,
    }),
  );

  const lineItemsById = keyBy(get($loadDetails).data?.lineItems, (it) => it.id);
  const receivedLineItemsById = keyBy(receivedLineItems, (it) => it.id);
  const localDeductionsById = keyBy(localDeductions, (it) => it.id);
  const remoteDeductionsById = keyBy(remoteDeductions, (it) => it.id);
  const deductionTypesById = get($deductionTypesById);

  return pipe(
    () => unionBy(localDeductions, remoteDeductions, (it) => it.id),
    (_) =>
      map(_, (item) => ({
        ...remoteDeductionsById[item.id],
        ...localDeductionsById[item.id],
      })),
    (_) => filter(_, (it) => !it.deleted),
    (_) =>
      map(_, (deduction) => {
        const deductionType = deductionTypesById[deduction.deductionTypeId];
        const isWeightDeduction = deductionType?.applicationType === 'Weight';

        const receivedLineItem = deduction.receivedLineItemId
          ? receivedLineItemsById[deduction.receivedLineItemId]
          : undefined;

        const lineItem = receivedLineItem?.lineItemId
          ? lineItemsById[receivedLineItem.lineItemId]
          : undefined;

        if (isWeightDeduction && deduction.weight) {
          const unitPrice =
            receivedLineItem?.unitPrice?.amount ??
            lineItem?.unitPrice?.amount ??
            0;

          const computedAmount = getWeightValue(deduction.weight) * unitPrice;

          return {
            ...deduction,
            computedAmount,
          };
        }

        return deduction;
      }),
    (_) => orderBy(_, (it) => it.createdAt),
  )();
});

export const $deductionsByReceivedLineItem = atom((get) =>
  groupBy(get($currentDeductions), (it) => it.receivedLineItemId ?? 'load'),
);

// 🟢🟢🟢 COMPUTED DATA -> net weight delta per line item
export const $lineItemsNetWeightDelta = atom((get) => {
  const lineItems = get($loadDetails).data?.lineItems;
  const localReceivedItems = get($receivedItemsByLineItem);

  const netWeightDeltaByLineItemId = pipe(
    () =>
      lineItems?.map((lineItem) => {
        const expectedNetQuantity = new Big(
          lineItem.weights?.['net']?.value ?? 0,
        );
        const receivedQuantity =
          localReceivedItems[lineItem.id]?.reduce(
            (acc, receivedMaterial) =>
              acc.plus(
                new Big(receivedMaterial.grossWeight?.value ?? 0).minus(
                  receivedMaterial.tareWeight?.value ?? 0,
                ),
              ),
            new Big(0),
          ) ?? new Big(0);

        return [
          lineItem.id,
          expectedNetQuantity.minus(receivedQuantity).toNumber(),
        ];
      }) ?? [],
    (_) => Object.fromEntries(_),
  )();

  return netWeightDeltaByLineItemId;
});

// 🟢🟢🟢 COMPUTED DATA -> total delta per line item
export const $lineItemsTotalValueDelta = atom((get) => {
  const lineItems = get($loadDetails).data?.lineItems;
  const localReceivedItems = get($receivedItemsByLineItem);

  const totalValueDeltaByLineItemId = Object.fromEntries(
    lineItems?.map((lineItem) => {
      const receivedMaterials = localReceivedItems[lineItem.id] ?? [];
      const expectedTotalValue = lineItem.estimatedPrice?.amount ?? 0;

      const receivedTotalValue =
        receivedMaterials
          ?.reduce((acc, receivedMaterial) => {
            const unitPrice = new Big(lineItem.unitPrice?.amount ?? 0);
            const netWeight = new Big(
              receivedMaterial.grossWeight?.value ?? 0,
            ).minus(receivedMaterial.tareWeight?.value ?? 0);

            return acc.plus(unitPrice.times(netWeight));
          }, new Big(0))
          .toNumber() ?? 0;

      return [lineItem.id, expectedTotalValue - receivedTotalValue];
    }) ?? [],
  );

  return totalValueDeltaByLineItemId;
});

// 🟢🟢🟢 COMPUTED DATA -> load totals
export const $loadTotals = atom((get) => {
  const lineItems = get($loadDetails).data?.lineItems;
  const receivedLineItems = get($receivedLineItems);

  const netShippedWeight = sumWeights(
    lineItems?.map((it) => ({
      value: new Big(it.weights?.['net']?.value ?? 0).toNumber(),
      unit: it.weights?.['net']?.unit ?? enumUnit.Pounds,
    })) ?? [],
  );

  const netReceivedWeight = sumWeights(
    receivedLineItems.map((it) => ({
      value: new Big(it.grossWeight?.value ?? 0)
        .minus(it.tareWeight?.value ?? 0)
        .toNumber(),
      unit: it.grossWeight?.unit ?? enumUnit.Pounds,
    })),
  );

  const netDeltaWeight = sumWeights([
    { value: netShippedWeight.value, unit: netShippedWeight.unit },
    { value: -netReceivedWeight.value, unit: netReceivedWeight.unit },
  ]);

  const shippedTotal =
    lineItems
      ?.reduce((acc, lineItem) => {
        const unitPrice = new Big(lineItem.unitPrice?.amount ?? 0);
        const netWeight = new Big(lineItem.weights?.['net']?.value ?? 0);
        return acc.plus(unitPrice.times(netWeight));
      }, new Big(0))
      .toNumber() ?? 0;

  const receivedTotal = receivedLineItems
    .reduce((acc, it) => {
      const unitPrice = new Big(it.unitPrice?.amount ?? 0);
      const netWeight = new Big(it.grossWeight?.value ?? 0).minus(
        it.tareWeight?.value ?? 0,
      );
      return acc.plus(unitPrice.times(netWeight));
    }, new Big(0))
    .toNumber();

  const deductionsTotal = get($currentDeductions).reduce(
    (acc, deduction) =>
      acc + (deduction.computedAmount ?? deduction.amount ?? 0),
    0,
  );

  return {
    netShippedWeight,
    netReceivedWeight,
    netDeltaWeight,
    shippedTotal,
    receivedTotal,
    deductionsTotal,
  };
});

// 🟢🟢🟢 COMPUTED DATA -> can edit reconciliation
export const $canEditReconciliation = atom((get) => {
  const loadDetails = get($loadDetails).data;
  return !loadDetails?.reviewed && loadDetails?.status === 'Received';
});

// 🟢🟢🟢 COMPUTED DATA -> has local changes
export const $hasLocalChanges = atom((get) => {
  const localReceivedItems = get($localReceivedItems);
  const localDeductions = get($localDeductions);

  return (
    localReceivedItems.length > 0 ||
    localDeductions.length > 0 ||
    localReceivedItems.some((it) => !it.synced) ||
    localDeductions.some((it) => !it.synced)
  );
});

// 🟢🟢🟢 COMPUTED DATA -> can save reconciliation
export const $canSaveReconciliation = atom((get) => {
  const canEditReconciliation = get($canEditReconciliation);
  const hasLocalChanges = get($hasLocalChanges);
  const receivedItems = get($receivedItemsByLineItem);
  const saveRemoteLoadReconciliation = get($saveRemoteLoadReconciliation);

  return (
    !saveRemoteLoadReconciliation.isPending &&
    canEditReconciliation &&
    hasLocalChanges &&
    Object.values(receivedItems)
      .flat()
      .every(
        (receivedMaterial) =>
          isDefined(receivedMaterial.grossWeight) &&
          isDefined(receivedMaterial.tareWeight) &&
          isDefined(receivedMaterial.materialId),
      )
  );
});

// 🟡🟡🟡 HIDDEN ACTION -> SAVE remote load reconciliation
export const $saveRemoteLoadReconciliation = loadReconciliationBatchUpdateAtom({
  onSuccess: (get, set) => {
    // Mark all local changes as synced
    set($localReceivedItems, (_) => _.map((it) => ({ ...it, synced: true })));
    set($localDeductions, (_) => _.map((it) => ({ ...it, synced: true })));

    // Refetch load details
    get($loadDetails)
      .refetch()
      .then(() => {
        set($localReceivedItems, []);
        set($localDeductions, []);
      });
  },
});

// 🟠🟠🟠 ACTION -> SAVE reconciliation
export const $saveLoadReconciliation = atom(null, (get, set) => {
  const loadId = get($loadId);
  const receivedLineItems = get($receivedLineItems);
  const receivedLineItemsById = keyBy(receivedLineItems, (it) => it.id);
  const lineItemsById = keyBy(get($loadDetails).data?.lineItems, (it) => it.id);

  const receivedLineItemsToSave = receivedLineItems.map((receivedItem) => {
    ensureValue(lineItemsById[receivedItem.lineItemId]);

    return {
      id: isLocalId(receivedItem.id) ? undefined : receivedItem.id,
      grossWeight: convertWeightToWeightInput(receivedItem.grossWeight),
      tareWeight: convertWeightToWeightInput(receivedItem.tareWeight),
      netWeight: convertWeightToWeightInput(receivedItem.netWeight),
      materialType: receivedItem.materialType ?? 'UNKNOWN',
      itemId: receivedItem.materialId,
      unitPrice: receivedItem.unitPrice ?? zeroMoney(),
      pricingUnit:
        'pricingUnit' in receivedItem
          ? (receivedItem.pricingUnit as Unit)
          : enumUnit.Pounds,
      loadLineItemId: receivedItem.lineItemId,
    };
  });

  const deductions = get($currentDeductions)
    .filter((it) => !it.deleted)
    .map((deduction) => ({
      id: isLocalId(deduction.id) ? undefined : deduction.id,
      deductionTypeId: deduction.deductionTypeId,
      priceQuantity: deduction.amount
        ? {
            amount: deduction.amount,
            currencyCode:
              receivedLineItemsById[ensureValue(deduction.receivedLineItemId)]
                ?.unitPrice?.currencyCode ?? enumCurrencyCode.USD,
          }
        : undefined,
      weightQuantity: convertWeightToWeightInput(deduction.weight),
      receivedLineItemId: deduction.receivedLineItemId,
    }));

  set($saveRemoteLoadReconciliation, {
    loadId,
    receivedLineItems: receivedLineItemsToSave,
    deductions,
  });
});

// 🟠🟠🟠 ACTION -> DISCARD reconciliation changes
export const $discardReconciliationChanges = atom(null, (get, set) => {
  set($localReceivedItems, []);
  set($localDeductions, []);
});

// 🟠🟠🟠 ACTION -> ADD load deduction
export const $addLoadDeduction = atom(
  null,
  (
    _get,
    set,
    {
      receivedLineItemId,
      deductionTypeId,
    }: { receivedLineItemId?: string; deductionTypeId: string },
  ) => {
    set($localDeductions, (prev) => [
      ...prev,
      {
        id: localId(),
        deductionTypeId,
        receivedLineItemId,
        createdAt: now().toString(),
      },
    ]);
  },
);

// 🟠🟠🟠 ACTION -> REMOVE load deduction
export const $removeLoadDeduction = atom(
  null,
  (get, set, { deductionId }: { deductionId: string }) => {
    const localDeductions = get($localDeductions);
    const currentDeductions = get($currentDeductions);
    const currentDeductionsById = keyBy(currentDeductions, (it) => it.id);

    const isPresentInLocalChanges = localDeductions.some(
      (it) => it.id === deductionId,
    );

    if (isPresentInLocalChanges) {
      set($localDeductions, (prev) =>
        prev.map((deduction) =>
          deduction.id === deductionId
            ? { ...deduction, deleted: true }
            : deduction,
        ),
      );
    } else {
      set($localDeductions, (prev) => [
        ...prev,
        { ...currentDeductionsById[deductionId], deleted: true },
      ]);
    }
  },
);

// 🟠🟠🟠 ACTION -> UPDATE load deduction
export const $updateLoadDeduction = atom(
  null,
  (
    _get,
    set,
    {
      deductionId,
      ...deductionParams
    }: {
      deductionId: string;
      deductionTypeId?: string;
      amount?: number;
      weight?: number | Weight | null;
    },
  ) => {
    set($localDeductions, (prev) =>
      prev.map((deduction) => {
        if (deduction.id === deductionId) {
          const { weight, ...otherParams } = deductionParams;
          const updatedDeduction = { ...deduction, ...otherParams };
          // Handle weight conversion
          if (weight !== undefined) {
            if (typeof weight === 'number') {
              updatedDeduction.weight = {
                value: weight,
                unit: enumUnit.Pounds,
                __typename: 'Weight' as const,
              };
            } else {
              updatedDeduction.weight = weight;
            }
          }
          return updatedDeduction;
        }
        return deduction;
      }),
    );
  },
);

// 🟠🟠🟠 ACTION -> ADD received material
export const $addReceivedMaterial = atom(
  null,
  (
    _get,
    set,
    {
      lineItemId,
      materialId,
      pricingUnit,
    }: { lineItemId: string; materialId?: string; pricingUnit?: Unit },
  ) => {
    set($localReceivedItems, (prev) => [
      ...prev,
      {
        id: localId(),
        lineItemId: lineItemId,
        materialId,
        grossWeight: {
          value: 0,
          unit: pricingUnit ?? enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
        tareWeight: {
          value: 0,
          unit: pricingUnit ?? enumUnit.Pounds,
          __typename: 'Weight' as const,
        },
        createdAt: now().toString(),
      },
    ]);
  },
);

// 🟠🟠🟠 ACTION -> REMOVE received material
export const $removeReceivedMaterial = atom(
  null,
  (get, set, { id, lineItemId }: { id: string; lineItemId: string }) => {
    const isPresentInLocalChanges = get($localReceivedItems).some(
      (it) => it.id === id,
    );

    set($localReceivedItems, (prev) =>
      isPresentInLocalChanges
        ? prev.map((it) => (it.id === id ? { ...it, deleted: true } : it))
        : [
            ...prev,
            {
              id,
              lineItemId,
              deleted: true,
            },
          ],
    );
  },
);

// 🟠🟠🟠 ACTION -> UPDATE received material
export const $updateReceivedMaterial = atom(
  null,
  (
    _get,
    set,
    {
      id,
      lineItemId,
      ...receivedMaterialParams
    }: {
      id: string;
      lineItemId: string;
      materialId?: string;
      grossWeight?: Weight | null;
      tareWeight?: Weight | null;
      unitPrice?: MoneyRecord;
    },
  ) => {
    set($localReceivedItems, (localReceivedItems) => {
      const isPresentInLocalChanges = localReceivedItems.some(
        (receivedMaterial) => receivedMaterial.id === id,
      );

      return isPresentInLocalChanges
        ? localReceivedItems.map((receivedMaterial) =>
            receivedMaterial.id === id
              ? { ...receivedMaterial, ...receivedMaterialParams }
              : receivedMaterial,
          )
        : [
            ...localReceivedItems,
            { id, lineItemId, ...receivedMaterialParams },
          ];
    });
  },
);
